import { UnlockPanelManager } from "@multiversx/sdk-dapp/out/managers/UnlockPanelManager/UnlockPanelManager";
import { getAccountProvider } from "@multiversx/sdk-dapp/out/providers/helpers/accountProvider";
import { useGetIsLoggedIn } from "@multiversx/sdk-dapp/out/react/account/useGetIsLoggedIn";
import { useGetLoginInfo } from "@multiversx/sdk-dapp/out/react/loginInfo/useGetLoginInfo";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, clearSession, getTgLink, setTgLink, type TgLinkInfo } from "./api";
import { getStartParam, tg } from "./telegram";
import { WALLETCONNECT_CONFIGURED, WALLETCONNECT_MISSING_MSG } from "./wallet";
import {
  CaveScreen,
  ChallengeAcceptScreen,
  ChallengeShareScreen,
  FightScreen,
  LeaderboardScreen,
  LobbyScreen,
  QueueScreen,
  RosterScreen,
  TournamentScreen,
} from "./screens";
import type {
  AvailableModes,
  BoardTab,
  Booga,
  BoogaDetail,
  ChallengeCreated,
  ChallengePreview,
  FightResponse,
  LeaderboardEntry,
  PvpFightResponse,
  PvpLeaderboardEntry,
  TournamentResponse,
} from "./types";

type View =
  | { name: "cave" }
  | { name: "roster" }
  | { name: "fight"; data: FightResponse; title: string; seed: number; rematch?: () => void }
  | { name: "tournament"; data: TournamentResponse }
  | { name: "leaderboard"; from: "cave" | "roster" }
  | { name: "challengeShare"; data: ChallengeCreated }
  | { name: "challengeAccept"; code: string; preview: ChallengePreview | null }
  | { name: "queue"; booga: Booga }
  | { name: "lobby" };

/** The bot users get their personal login link from (VITE_SHARE_URL = t.me/<bot>). */
const BOT_URL: string = import.meta.env.VITE_SHARE_URL ?? "https://t.me/BoogaBrawlBot";

/** `?tg=CODE` — a bot-issued one-time login code on the URL we were opened with. */
function tgCodeFromUrl(): string | null {
  const raw = new URLSearchParams(window.location.search).get("tg");
  return raw ? raw.toUpperCase().replace(/[^A-Z0-9]/g, "") : null;
}

/** Drop `tg=` from the address bar (keeps startapp & co) so a reload/share can't replay it. */
function stripTgCodeFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("tg")) return;
  url.searchParams.delete("tg");
  window.history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
}

// The challenger's open challenge, per tab: survives a refresh so they come
// back to "Challenge sent" (or straight to the brawl once it's been accepted).
const MY_CHALLENGE_KEY = "bb_my_challenge";
function loadMyChallenge(): ChallengeCreated | null {
  try {
    const raw = sessionStorage.getItem(MY_CHALLENGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as ChallengeCreated;
    return c.expiresAt * 1000 > Date.now() ? c : null;
  } catch {
    return null;
  }
}
function saveMyChallenge(c: ChallengeCreated | null): void {
  try {
    if (c) sessionStorage.setItem(MY_CHALLENGE_KEY, JSON.stringify(c));
    else sessionStorage.removeItem(MY_CHALLENGE_KEY);
  } catch {
    /* storage blocked — the bot DM remains the fallback */
  }
}

const randomSeed = () => Math.floor(Math.random() * 2 ** 31);
const errMsg = (e: unknown) =>
  e instanceof Error ? e.message : "Something grunked. Try again.";

export function App() {
  const [view, setView] = useState<View>({ name: "cave" });
  const [roster, setRoster] = useState<Booga[]>([]);
  const [modes, setModes] = useState<AvailableModes>({
    poolFight: false,
    oneVsOne: false,
    tournament: false,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [lb, setLb] = useState<LeaderboardEntry[]>([]);
  const [lbLoading, setLbLoading] = useState(false);
  const [lbMore, setLbMore] = useState(false);
  const [lbHasMore, setLbHasMore] = useState(false);
  const [lbTab, setLbTab] = useState<BoardTab>("all");
  const [pvpLb, setPvpLb] = useState<PvpLeaderboardEntry[]>([]);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BoogaDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Auto-dismiss transient errors on non-cave screens (cave shows them inline).
  useEffect(() => {
    if (!error || view.name === "cave") return;
    const t = setTimeout(() => setError(undefined), 4000);
    return () => clearTimeout(t);
  }, [error, view.name]);

  const nameOf = useCallback(
    (id: string | null) =>
      !id ? "—" : (roster.find((b) => b.identifier === id)?.name ?? id),
    [roster],
  );

  // sdk-dapp manages the wallet login and exposes the native-auth token here.
  const { tokenLogin } = useGetLoginInfo();
  const nativeAuthToken = tokenLogin?.nativeAuthToken;
  const isDappLoggedIn = useGetIsLoggedIn();
  const [authed, setAuthed] = useState(false);
  const loginInFlight = useRef(false);
  // Whether the current session came from sdk-dapp (wallet UI) — only then does
  // an sdk-dapp logout mean "wallet disconnected". Dev logins (?dev=) don't.
  const viaDapp = useRef(false);

  // Telegram identity for this browser (bot doorway). Claimed from `?tg=CODE`
  // on first open, then kept in localStorage for weeks.
  const [tgLink, setTgLinkState] = useState<TgLinkInfo | null>(() => getTgLink());
  // While a `?tg=` code is being claimed, hold the wallet login back: the first
  // login after opening a bot link must carry the Telegram identity, not race it.
  const [claimingTgCode, setClaimingTgCode] = useState<boolean>(() => tgCodeFromUrl() !== null);
  useEffect(() => {
    const code = tgCodeFromUrl();
    if (!code) return;
    stripTgCodeFromUrl();
    api
      .claimTgCode(code)
      .then((info) => {
        setTgLinkState(info);
        setError(undefined);
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setClaimingTgCode(false));
  }, []);
  const unlinkTelegram = () => {
    setTgLink(null);
    setTgLinkState(null);
  };

  // PvP deep-link payload (challenge `c_…` consumed after login; replay `f_…`
  // consumed immediately). Read once — Telegram never changes it mid-session.
  const pendingRoute = useRef<string | null>(getStartParam());

  // Drop the local session and land back on the cave screen. Idempotent — safe
  // to call from both our own logout and wallet-side disconnect events.
  const resetToCave = useCallback((message?: string) => {
    clearSession();
    setAuthed(false);
    setRoster([]);
    setModes({ poolFight: false, oneVsOne: false, tournament: false });
    setView({ name: "cave" });
    setError(message);
  }, []);

  // sdk-dapp noticed the wallet side logged out (or the user logged out through
  // its UI) → mirror it here.
  useEffect(() => {
    if (authed && viaDapp.current && !isDappLoggedIn) {
      resetToCave("Wallet disconnected. Connect again to brawl.");
    }
  }, [authed, isDappLoggedIn, resetToCave]);

  // User-initiated logout (back arrow on the roster): kill the wallet link and
  // the local session. The Telegram link stays — it's identity, not a session.
  function logoutApp() {
    if (isDappLoggedIn) {
      void getAccountProvider().logout().catch(() => {});
    }
    resetToCave();
  }

  // Open the accept screen for a challenge deep link. Public — the preview
  // renders before login; the screen swaps its CTA based on `authed`.
  const openChallenge = useCallback(async (code: string) => {
    setView({ name: "challengeAccept", code, preview: null });
    try {
      const preview = await api.challengePreview(code);
      setView({ name: "challengeAccept", code, preview });
    } catch (e) {
      pendingRoute.current = null;
      setView({ name: "cave" });
      setError(errMsg(e));
    }
  }, []);

  // Consume a PvP deep link on boot: a replay opens immediately (public); a
  // challenge shows its preview (accepting requires login, handled in-screen).
  useEffect(() => {
    const payload = pendingRoute.current;
    if (!payload) return;
    if (payload.startsWith("f_")) {
      pendingRoute.current = null;
      const id = Number(payload.slice(2));
      if (!Number.isInteger(id) || id <= 0) return;
      api
        .fightById(id)
        .then((data) =>
          setView({ name: "fight", data, title: "📜 Brawl Replay", seed: data.fight.seed }),
        )
        .catch((e) => setError(errMsg(e)));
    } else if (payload.startsWith("c_")) {
      void openChallenge(payload.slice(2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Exchange a native-auth token + proof of Telegram identity (the Telegram
  // link; legacy initData if we happen to run inside Telegram) for a server
  // session, then load the proven roster. Guarded so it runs once.
  // DEV ONLY: `?dev=erd1…` logs in as that wallet (server must run with
  // DEV_ALLOW_NO_INITDATA=1; the route 404s otherwise). Two tabs, two holders,
  // real rosters — the way to exercise PvP locally without two xPortals.
  useEffect(() => {
    const url = new URL(window.location.href);
    const address = url.searchParams.get("dev");
    if (!address) return;
    url.searchParams.delete("dev");
    window.history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
    setBusy(true);
    api
      .devLogin(address)
      .then(() => {
        viaDapp.current = false;
        return enterWithSession();
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // With a session stored, load the proven roster and land on it (or on a
  // pending challenge deep link).
  const enterWithSession = useCallback(async () => {
    const res = await api.roster();
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setAuthed(true);
    setRoster(res.roster);
    setModes(res.modes);
    // A pending challenge deep link outranks the roster landing.
    const payload = pendingRoute.current;
    if (payload?.startsWith("c_")) {
      void openChallenge(payload.slice(2));
      return;
    }
    // Refresh while a challenge of ours is out: land back on it (the poll
    // effect on that screen then notices if it has been accepted meanwhile).
    const mine = loadMyChallenge();
    if (mine) {
      setView({ name: "challengeShare", data: mine });
      return;
    }
    setView({ name: "roster" });
  }, [openChallenge]);

  const finishLogin = useCallback(
    async (token: string) => {
      if (loginInFlight.current) return;
      loginInFlight.current = true;
      setBusy(true);
      setError(undefined);
      try {
        await api.login(token, {
          tgLink: getTgLink()?.token,
          initData: tg?.initData || undefined,
        });
        viaDapp.current = true;
        await enterWithSession();
      } catch (e) {
        setError(errMsg(e));
      } finally {
        setBusy(false);
        loginInFlight.current = false;
      }
    },
    [enterWithSession],
  );

  // Connect: sdk-dapp's unlock panel (xPortal QR / xPortal Hub webview / DeFi
  // extension / web wallet). The effect below finishes login when its token
  // lands. Wallet UI never runs inside Telegram — see wallet.ts.
  function connectWallet() {
    setError(undefined);
    // Without a projectId sdk-dapp's xPortal option silently does nothing
    // (see wallet.ts). Say so; the panel's other providers still work.
    if (!WALLETCONNECT_CONFIGURED) setError(WALLETCONNECT_MISSING_MSG);
    const manager = UnlockPanelManager.init({ loginHandler: () => undefined });
    void manager.openUnlockPanel();
  }

  // Finish login when sdk-dapp surfaces a token (fresh login or a session
  // restored from sessionStorage). Waits for a pending `?tg=` claim so the
  // first login after opening a bot link carries the Telegram identity.
  useEffect(() => {
    if (!nativeAuthToken || authed || claimingTgCode) return;
    void finishLogin(nativeAuthToken);
  }, [nativeAuthToken, authed, claimingTgCode, tgLink, finishLogin]);

  async function run<T>(work: () => Promise<T>, onOk: (result: T) => void) {
    setBusy(true);
    setError(undefined);
    try {
      onOk(await work());
    } catch (e) {
      // Server session expired mid-play → back to the cave to reconnect.
      if (e instanceof ApiError && e.status === 401) {
        resetToCave("Session expired. Connect again, fam.");
        return;
      }
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  function doFight(a: string, b: string) {
    const seed = randomSeed();
    void run(
      () => api.fight(a, b, seed),
      (data) =>
        setView({ name: "fight", data, title: "⚔️ 1v1 Brawl", seed, rematch: () => doFight(a, b) }),
    );
  }

  function doPool(id: string) {
    const seed = randomSeed();
    void run(
      () => api.poolFight(id, seed),
      (data) =>
        setView({ name: "fight", data, title: "🎲 Pool Brawl", seed, rematch: () => doPool(id) }),
    );
  }

  function doTournament(ids: string[]) {
    void run(
      () => api.tournament(ids, randomSeed()),
      (data) => setView({ name: "tournament", data }),
    );
  }

  // --- PvP -------------------------------------------------------------------

  // Challenger side: while "Challenge sent" is up, watch the challenge. The
  // moment a fren accepts, open the brawl right here instead of making the
  // challenger wait for the bot's DM. Expired/cancelled → back to the roster.
  useEffect(() => {
    if (view.name !== "challengeShare") return;
    const code = view.data.code;
    let stopped = false;
    const tick = async () => {
      try {
        const p = await api.challengePreview(code);
        if (stopped) return;
        if (p.status === "accepted" && p.fightId) {
          stopped = true;
          saveMyChallenge(null);
          const data = await api.fightById(p.fightId);
          if (stopped && view.name !== "challengeShare") return;
          setView({ name: "fight", data, title: "⚔️ PvP Brawl", seed: data.fight.seed });
        } else if (p.status === "expired" || p.status === "cancelled") {
          stopped = true;
          saveMyChallenge(null);
          setError(p.status === "expired" ? "Your challenge expired, fam." : undefined);
          setView({ name: "roster" });
        }
      } catch {
        /* transient — try again next tick */
      }
    };
    const timer = setInterval(() => void tick(), 3000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [view]);

  function doChallengeCreate(boogaId: string, targetTgId?: number) {
    void run(
      () => api.createChallenge(boogaId, targetTgId),
      (data) => {
        saveMyChallenge(data);
        setView({ name: "challengeShare", data });
      },
    );
  }

  function doChallengeAccept(code: string, boogaId: string) {
    pendingRoute.current = null;
    const seed = randomSeed();
    void run(
      () => api.acceptChallenge(code, boogaId, seed),
      (data) => setView({ name: "fight", data, title: "⚔️ PvP Brawl", seed }),
    );
  }

  function doChallengeCancel(code: string) {
    void run(
      () => api.cancelChallenge(code),
      () => setView({ name: "roster" }),
    );
  }

  function openQueue(booga: Booga) {
    setView({ name: "queue", booga });
  }

  function onQueueMatched(data: PvpFightResponse) {
    setView({ name: "fight", data, title: "⚔️ PvP Brawl", seed: data.fight.seed });
  }

  const LB_PAGE = 25;

  // Load page 0 for a tab; also switches the active tab (toggle handler).
  function loadBoard(tab: BoardTab) {
    setLbTab(tab);
    setLb([]);
    setPvpLb([]);
    setLbHasMore(false);
    setLbLoading(true);
    const work =
      tab === "pvp"
        ? api.pvpLeaderboard(100, 0).then(setPvpLb)
        : api.leaderboard(tab, LB_PAGE, 0).then((page) => {
            setLb(page);
            setLbHasMore(page.length === LB_PAGE);
          });
    work.catch((e) => setError(errMsg(e))).finally(() => setLbLoading(false));
  }

  function openLeaderboard(from: "cave" | "roster") {
    setView({ name: "leaderboard", from });
    loadBoard("all");
  }

  function loadMoreLeaderboard() {
    if (lbLoading || lbMore || !lbHasMore || lbTab === "pvp") return;
    setLbMore(true);
    api
      .leaderboard(lbTab, LB_PAGE, lb.length)
      .then((page) => {
        setLb((prev) => [...prev, ...page]);
        setLbHasMore(page.length === LB_PAGE);
      })
      .catch((e) => setError(errMsg(e)))
      .finally(() => setLbMore(false));
  }

  function openDetail(id: string) {
    setDetailId(id);
    setDetail(null);
    setDetailLoading(true);
    api
      .boogaDetail(id)
      .then(setDetail)
      .catch((e) => setError(errMsg(e)))
      .finally(() => setDetailLoading(false));
  }

  function closeDetail() {
    setDetailId(null);
    setDetail(null);
  }

  const toRoster = () => setView({ name: "roster" });

  return (
    <div className="app">
      {error && view.name !== "cave" ? (
        <div className="toast" onClick={() => setError(undefined)}>
          {error}
        </div>
      ) : null}

      {view.name === "cave" && (
        <CaveScreen
          onConnect={connectWallet}
          onLeaderboard={() => openLeaderboard("cave")}
          loading={busy}
          error={error}
          telegram={{
            linked: tgLink !== null,
            username: tgLink?.username ?? null,
            botUrl: BOT_URL,
            onUnlink: unlinkTelegram,
          }}
        />
      )}

      {view.name === "roster" && (
        <RosterScreen
          roster={roster}
          modes={modes}
          busy={busy}
          onOneVsOne={doFight}
          onPool={doPool}
          onTournament={doTournament}
          onChallenge={doChallengeCreate}
          onQueue={(id) => {
            const b = roster.find((x) => x.identifier === id);
            if (b) openQueue(b);
          }}
          onLobby={() => setView({ name: "lobby" })}
          onLeaderboard={() => openLeaderboard("roster")}
          onBack={logoutApp}
        />
      )}

      {view.name === "lobby" && (
        <LobbyScreen
          roster={roster}
          busy={busy}
          onAcceptChallenge={(code) => void openChallenge(code)}
          onChallengePlayer={(tgId, boogaId) => doChallengeCreate(boogaId, tgId)}
          onSessionExpired={() => resetToCave("Session expired. Connect again, fam.")}
          onExit={toRoster}
        />
      )}

      {view.name === "challengeShare" && (
        <ChallengeShareScreen
          data={view.data}
          busy={busy}
          onCancel={() => {
            saveMyChallenge(null);
            doChallengeCancel(view.data.code);
          }}
          onBack={toRoster}
        />
      )}

      {view.name === "challengeAccept" && (
        <ChallengeAcceptScreen
          preview={view.preview}
          authed={authed}
          roster={roster}
          busy={busy}
          onConnect={connectWallet}
          onAccept={(boogaId) => doChallengeAccept(view.code, boogaId)}
          onWatchReplay={(fightId) =>
            void run(
              () => api.fightById(fightId),
              (data) =>
                setView({ name: "fight", data, title: "📜 Brawl Replay", seed: data.fight.seed }),
            )
          }
          onExit={() => {
            pendingRoute.current = null;
            setView(authed ? { name: "roster" } : { name: "cave" });
          }}
        />
      )}

      {view.name === "queue" && (
        <QueueScreen
          booga={view.booga}
          onMatched={onQueueMatched}
          onSessionExpired={() => resetToCave("Session expired. Connect again, fam.")}
          onExit={toRoster}
        />
      )}

      {view.name === "fight" && (
        <FightScreen
          key={view.seed}
          data={view.data}
          title={view.title}
          onRematch={view.rematch}
          onExit={authed ? toRoster : () => setView({ name: "cave" })}
        />
      )}

      {view.name === "tournament" && (
        <TournamentScreen data={view.data} nameOf={nameOf} onExit={toRoster} />
      )}

      {view.name === "leaderboard" && (
        <LeaderboardScreen
          entries={lb}
          pvpEntries={pvpLb}
          loading={lbLoading}
          loadingMore={lbMore}
          hasMore={lbHasMore}
          onLoadMore={loadMoreLeaderboard}
          tab={lbTab}
          onTab={loadBoard}
          onOpenDetail={openDetail}
          detailOpen={detailId !== null}
          detail={detail}
          detailLoading={detailLoading}
          onCloseDetail={closeDetail}
          onBack={() =>
            setView(view.from === "roster" ? { name: "roster" } : { name: "cave" })
          }
        />
      )}
    </div>
  );
}
